/**
 * Astitva Living — Scroll Animation Controller
 * Handles: IntersectionObserver reveals, parallax, stagger children
 * Respects prefers-reduced-motion.
 * Loaded conditionally based on settings.enable_animations.
 */

(function () {
  'use strict';

  /* ── Reduced motion check ── */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =============================================
     SCROLL REVEAL (data-reveal, data-stagger)
  ============================================= */
  const Reveal = {
    observer: null,
    VISIBLE_CLASS: 'is-visible',

    init() {
      if (prefersReducedMotion) {
        // Immediately make everything visible
        document.querySelectorAll('[data-reveal], [data-stagger]').forEach(el => {
          el.classList.add(this.VISIBLE_CLASS);
        });
        return;
      }

      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          threshold: 0.12,
          rootMargin: '0px 0px -48px 0px'
        }
      );

      // Observe all reveal elements
      document.querySelectorAll('[data-reveal], [data-stagger]').forEach(el => {
        this.observer.observe(el);
      });
    },

    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(this.VISIBLE_CLASS);
          // Unobserve after first reveal (one-shot)
          this.observer.unobserve(entry.target);
        }
      });
    },

    // Call this after dynamic content is injected (AJAX)
    refresh() {
      if (!this.observer || prefersReducedMotion) return;
      document.querySelectorAll('[data-reveal]:not(.is-visible), [data-stagger]:not(.is-visible)').forEach(el => {
        this.observer.observe(el);
      });
    }
  };

  /* =============================================
     PARALLAX (hero images)
  ============================================= */
  const Parallax = {
    elements: [],
    rafId: null,
    lastScrollY: 0,

    init() {
      if (prefersReducedMotion) return;

      this.elements = [...document.querySelectorAll('[data-parallax]')];
      if (!this.elements.length) return;

      window.addEventListener('scroll', this.scheduleUpdate.bind(this), { passive: true });
      this.update();
    },

    scheduleUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.update();
        this.rafId = null;
      });
    },

    update() {
      const scrollY = window.scrollY;
      this.elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.3;
        const offset = (rect.top + scrollY) * speed;
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;

        if (inView) {
          el.style.transform = `translateY(${offset * 0.2}px)`;
        }
      });
    }
  };

  /* =============================================
     COUNTER ANIMATION (stats strip)
  ============================================= */
  const Counter = {
    observer: null,

    init() {
      if (prefersReducedMotion) return;

      const counters = document.querySelectorAll('[data-counter]');
      if (!counters.length) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCounter(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });

      counters.forEach(el => this.observer.observe(el));
    },

    animateCounter(el) {
      const rawTarget = el.dataset.counter;
      const isFloat = rawTarget.includes('.');
      const target = parseFloat(rawTarget);
      const duration = 1600;
      const suffix = el.dataset.counterSuffix || '';
      const prefix = el.dataset.counterPrefix || '';
      const start = performance.now();

      const step = (timestamp) => {
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out quad
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = eased * target;

        const formattedValue = isFloat 
          ? value.toFixed(1) 
          : Math.floor(value).toLocaleString('en-IN');

        el.textContent = prefix + formattedValue + suffix;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.textContent = prefix + (isFloat ? target.toFixed(1) : target.toLocaleString('en-IN')) + suffix;
        }
      };

      requestAnimationFrame(step);
    }
  };

  /* =============================================
     MARQUEE (text strip)
  ============================================= */
  const Marquee = {
    init() {
      document.querySelectorAll('[data-marquee]').forEach(el => {
        // Pause on hover / focus-within
        el.addEventListener('mouseenter', () => {
          el.style.animationPlayState = 'paused';
          el.querySelectorAll('[data-marquee-track]').forEach(track => {
            track.style.animationPlayState = 'paused';
          });
        });

        el.addEventListener('mouseleave', () => {
          el.style.animationPlayState = 'running';
          el.querySelectorAll('[data-marquee-track]').forEach(track => {
            track.style.animationPlayState = 'running';
          });
        });
      });
    }
  };

  /* =============================================
     IMAGE HOVER SWAP (product cards)
  ============================================= */
  const ImageSwap = {
    init() {
      document.querySelectorAll('[data-image-swap]').forEach(card => {
        const primary = card.querySelector('[data-img-primary]');
        const hover   = card.querySelector('[data-img-hover]');
        if (!primary || !hover) return;

        card.addEventListener('mouseenter', () => {
          primary.style.opacity = '0';
          hover.style.opacity   = '1';
        });

        card.addEventListener('mouseleave', () => {
          primary.style.opacity = '1';
          hover.style.opacity   = '0';
        });
      });
    }
  };

  /* =============================================
     INIT
  ============================================= */
  function init() {
    Reveal.init();
    Parallax.init();
    Counter.init();
    Marquee.init();
    ImageSwap.init();
  }

  // Expose refresh for AJAX content
  window.AstitvaAnimations = { refresh: () => Reveal.refresh() };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
